
import { useState } from "react";
import { Shell } from "@/components/layout/Shell";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useContractTypes } from "@/hooks/useContractTypes";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { ContractTypeDeleteDialog } from "@/components/contract-types/ContractTypeDeleteDialog";
import { ContractType } from "@/lib/types";

export default function ContractTypesPage() {
  const { useContractTypesQuery } = useContractTypes();
  const { data: contractTypes, isLoading, error } = useContractTypesQuery();
  const [contractTypeToDelete, setContractTypeToDelete] = useState<ContractType | null>(null);

  const handleDeleteClick = (type: ContractType) => {
    console.log("Setting contract type to delete:", type);
    setContractTypeToDelete(type);
  };

  return (
    <Shell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Contract Types</h1>
            <p className="text-muted-foreground mt-2">
              Manage contract types
            </p>
          </div>
          <Button asChild>
            <Link to="/contract-types/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Contract Type
            </Link>
          </Button>
        </div>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg border bg-card shadow-sm p-6 space-y-4">
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
                <div className="flex justify-end">
                  <Skeleton className="h-9 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load contract types: {(error as Error).message}
            </AlertDescription>
          </Alert>
        ) : contractTypes && contractTypes.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {contractTypes.map((type) => (
              <div
                key={type.id}
                className="rounded-lg border bg-card shadow-sm transition-all hover:shadow-md animate-fade-in"
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold">{type.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {type.description || "No description provided"}
                  </p>
                  <div className="flex justify-end mt-4 gap-2">
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDeleteClick(type)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/contract-types/${type.id}`}>Manage</Link>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No contract types found. Create your first one!</p>
          </div>
        )}
      </div>

      {contractTypeToDelete && (
        <ContractTypeDeleteDialog
          contractType={contractTypeToDelete}
          open={!!contractTypeToDelete}
          onOpenChange={(open) => {
            if (!open) setContractTypeToDelete(null);
          }}
        />
      )}
    </Shell>
  );
}
